import React, { useEffect } from 'react';
import styles from '@/styles/Headline.module.scss';
import { PEIHeadLine } from '@/common/components/PEIHeadline';
import { HeadLineItems } from './headlineItem';
import { URL, buildRelatedNewsSlug } from '@/utils';
import { defaultImagePath } from '@/common';
import { useAppContext } from '@/components';
import { ZephrFeature } from '@/components/ZephrFeature';
import { Pagination } from '@/components/sections/RelatedNewsListing/Pagination';

const { basePEIURL } = URL;

type headlineItemsProp = {
  image: string;
  title: string;
  summary: string;
  type: string;
  postName: string;
  thumbnail: string;
};

type headlineProp = {
  institutionID: string;
  institutionName: string;
  userType: string;
  partnerStatus: string;
  aboutItems: headlineItemsProp[];
  total?: number;
  // WEB-27455: total pages of related news; the pager links to the standalone
  // paginated listing pages (page 1 = the block itself).
  relatedNewsPageCount?: number;
  institutionSlug?: string;
};

export const HeadlinesPlaceholder = ({
  institutionName,
  showImages = true,
}: {
  institutionName: string;
  showImages?: boolean;
}) => (
  <div className={styles.galleyContainer}>
    {[1, 2, 3].map((id) => (
      <HeadLineItems
        key={id + 'placeholder'}
        imageURL={defaultImagePath}
        headingText={`Sample story headline about ${institutionName}`}
        contentText='Subscribe to read the latest news, insights and analysis from our editorial team covering this institution and its activity across private markets.'
        link='#'
        showImages={showImages}
      />
    ))}
  </div>
);

export const Headlines = ({
  aboutItems,
  institutionName,
  relatedNewsPageCount,
  institutionSlug,
}: headlineProp) => {
  const { dispatch } = useAppContext();

  useEffect(() => {
    dispatch({
      type: 'setRecordInfo',
      payload: {
        key: 'headlines',
        value: aboutItems.length > 0,
      },
    });
  }, [dispatch, aboutItems]);

  return (
    <>
      {aboutItems.length > 0 && (
        <article className={styles.container} id='headlines'>
          <main className={styles['container-body']}>
            <PEIHeadLine text={`Stories about ${institutionName}`} />
            <ZephrFeature
              placeholder={<HeadlinesPlaceholder institutionName={institutionName} />}
            >
              <div className={styles.galleyContainer}>
                {aboutItems.map((item, id) => (
                  <HeadLineItems
                    key={id + 'about'}
                    imageURL={item?.thumbnail ? item?.thumbnail : defaultImagePath}
                    headingText={item.title}
                    contentText={item.summary}
                    link={`${basePEIURL}/${item?.postName}`}
                  />
                ))}
              </div>
            </ZephrFeature>
            {relatedNewsPageCount && relatedNewsPageCount > 1 && institutionSlug && (
              <Pagination
                currentPage={1}
                pageCount={relatedNewsPageCount}
                // Relative: the listing pages sit next to this one in
                // /institution-profiles/, so the link follows the host the visitor is
                // already on rather than pinning them to basePEIURL (WEB-27455 QA #3).
                buildHref={(p) => `${buildRelatedNewsSlug(institutionSlug, p)}.html`}
              />
            )}
          </main>
        </article>
      )}
    </>
  );
};
